import { clsx } from 'clsx';
import React, { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Input field name
   */
  name: string;

  /**
   * Input type
   * @default 'text'
   */
  type?: 'text' | 'email' | 'password' | 'tel' | 'number' | 'url';

  /**
   * Whether input has error
   * @default false
   */
  error?: boolean;

  /**
   * Helper text or error message
   */
  helperText?: string;

  /**
   * Full width input
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Icon to display on the left side of the input
   */
  icon?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      error = false,
      helperText,
      fullWidth = false,
      className,
      disabled,
      icon,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPasswordType = type === 'password';
    const actualType = isPasswordType && showPassword ? 'text' : type;

    const baseStyles =
      'py-2 text-base rounded-lg transition-all duration-200 border focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:bg-neutral-100 disabled:cursor-not-allowed disabled:text-neutral-500';

    const errorStyles = error
      ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500'
      : 'border-neutral-300 focus:border-purple-500 focus:ring-purple-500 hover:border-neutral-400';

    const widthStyle = fullWidth ? 'w-full' : '';
    const paddingStyles = icon ? 'pl-10 pr-4' : 'px-4';

    return (
      <div className={clsx(widthStyle)}>
        <div className="relative">
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
              {icon}
            </div>
          )}

          <input
            ref={ref}
            type={actualType}
            disabled={disabled}
            className={clsx(
              baseStyles,
              errorStyles,
              widthStyle,
              paddingStyles,
              isPasswordType && 'pr-10',
              className
            )}
            aria-invalid={error}
            aria-describedby={helperText ? `${props.name}-helper` : undefined}
            {...props}
          />

          {isPasswordType && (
            <button
              type="button"
              onClick={() => {
                setShowPassword(!showPassword);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700 focus:outline-none focus:text-neutral-700 transition-colors"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              tabIndex={-1}
            >
              {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
            </button>
          )}
        </div>

        {helperText && (
          <p
            id={`${props.name}-helper`}
            className={clsx('mt-1.5 text-sm', error ? 'text-danger-600' : 'text-neutral-600')}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
